import { useState } from 'react';
import type { IRepoQualityRow, SonarGrade } from '@skystone/shared';
import { Badge } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/use-media-query';
import { RankedPanelShell } from './RankedPanelShell';
import { RepoQualityRow } from './RepoQualityRow';

interface WorstReliabilityPanelProps {
  rows: IRepoQualityRow[];
}

function GradeChip({ grade }: Readonly<{ grade: SonarGrade | null }>) {
  if (grade === null) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const variant = grade === 'C' ? 'warning' : 'error';
  // Stronger contrast for E (per spec)
  const extra = grade === 'E' ? 'ring-1 ring-error-300' : '';
  return (
    <Badge variant={variant} className={cn('font-mono text-[10px]', extra)}>
      {grade}
    </Badge>
  );
}

/**
 * Lists repos at reliability grade C/D/E (excludes A/B by definition). Shows
 * the affirmative empty state when the entire portfolio is A/B.
 */
export function WorstReliabilityPanel({ rows }: Readonly<WorstReliabilityPanelProps>) {
  const [expanded, setExpanded] = useState(false);
  const isSmUp = useMediaQuery('(min-width: 640px)');
  const defaultLimit = isSmUp ? 10 : 5;
  const visibleRows = expanded ? rows : rows.slice(0, defaultLimit);

  return (
    <RankedPanelShell
      testId="worst-reliability-panel"
      title="Worst reliability grade"
      countLabel={`(${rows.length})`}
      metricKey="cq_reliability_rating"
      totalRows={rows.length}
      defaultLimit={defaultLimit}
      expanded={expanded}
      onToggleExpanded={() => setExpanded((v) => !v)}
      emptyState={rows.length === 0 ? 'All in-scope repos at grade A or B — nice.' : null}
    >
      {visibleRows.map((row) => (
        <RepoQualityRow
          key={row.repositoryId}
          repositoryId={row.repositoryId}
          repositoryName={row.repositoryName}
          projectKey={row.projectKey}
          organization={row.organization}
          lastAnalysedAt={row.lastAnalysedAt}
          valueSlot={<GradeChip grade={row.reliabilityGrade} />}
        />
      ))}
    </RankedPanelShell>
  );
}
